/**
 * The card a tapped concept raises over the phone graph - the map-pin
 * pattern: say what you touched, and offer the next step, without leaving
 * the map.
 *
 * Desktop opens the concept panel straight from a click, because the panel
 * sits BESIDE the graph there. On a phone the panel is full screen, so a tap
 * that opened it would cover the map the user was exploring, every time,
 * including the taps that only meant "what is this dot?". The peek answers
 * that question - name, domain, how many concepts it links to - while the
 * graph behind it lights that concept's links. "Open concept" is the step
 * into the panel.
 *
 * `aria-live` so the change is announced: the canvas tap that raised it has
 * no DOM of its own to move focus from.
 */

import { ArrowRight, X } from 'lucide-react';
import { VisualThumb, hasThumb } from '@/components/core/visual';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import type { KnowledgeGraphNode } from '@/lib/api-client';

interface ConceptPeekCardProps {
  concept: KnowledgeGraphNode;
  /** How many concepts it links to in the graph on screen. */
  links: number;
  onOpen: () => void;
  onClose: () => void;
}

export function ConceptPeekCard({
  concept,
  links,
  onOpen,
  onClose,
}: ConceptPeekCardProps) {
  const domains = concept.domain_tags?.filter(Boolean) ?? [];
  return (
    <div className='pointer-events-none absolute inset-x-0 bottom-0 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]'>
      <section
        // Keyed by the caller on the concept, so a second tap re-runs the
        // rise instead of swapping text in place. A rise, no fade: a card
        // that starts transparent reads as absent to anything that looks
        // (an eye at a glance, a visibility check) for its first frames.
        aria-label='Selected concept'
        aria-live='polite'
        className='pointer-events-auto relative isolate rounded-3xl border border-border bg-card p-4 shadow-elevation-2 motion-safe:duration-200 motion-safe:animate-in motion-safe:slide-in-from-bottom-4'
      >
        <button
          type='button'
          onClick={onClose}
          aria-label='Close concept preview'
          className='absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          <X className='h-4 w-4' aria-hidden='true' />
        </button>
        <p className='font-mono text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground'>
          {links === 0
            ? 'No links yet'
            : `Linked to ${links} concept${links === 1 ? '' : 's'}`}
        </p>
        <div className='mt-1 flex items-center gap-3 pr-10'>
          {concept.visual && hasThumb(concept.visual) && (
            <VisualThumb
              visual={concept.visual}
              className='h-14 w-14 shrink-0'
            />
          )}
          <div className='min-w-0'>
            <h3 className='text-lg font-semibold leading-snug text-foreground'>
              <InlineMarkdown>{concept.name}</InlineMarkdown>
            </h3>
            {domains.length > 0 && (
              <p className='mt-0.5 truncate text-sm text-muted-foreground'>
                {domains.join(' · ')}
              </p>
            )}
          </div>
        </div>
        <button
          type='button'
          onClick={onOpen}
          className='mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-semibold text-primary-foreground transition-transform duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card active:scale-[0.98] motion-reduce:active:scale-100'
        >
          Open concept
          <ArrowRight className='h-4 w-4' aria-hidden='true' />
        </button>
      </section>
    </div>
  );
}
